"use client"

import { convertToLocale } from "@lib/util/money"
import { getProductUrl, isVariantInStock } from "@lib/util/product"
import { getShippingEstimate } from "@lib/util/shipping-estimate"
import { getCheckoutStep } from "@lib/util/get-checkout-step"
import { groupCustomizationFees } from "@lib/util/cart-line-items"
import { updateLineItem } from "@lib/data/cart"
import { listProducts } from "@lib/data/products"
import { HttpTypes } from "@medusajs/types"
import { CART_PROMO_SLIDES, CART_DRAWER_OPEN_EVENT } from "@constants/cart"
import { getSizeFromVariant } from "@modules/cart/utils"
import DeleteButton from "@modules/common/components/delete-button"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import Thumbnail from "@modules/products/components/thumbnail"
import MysteryBox from "@modules/cart/components/mystery-box"
import LineItemCustomizationEditor from "@modules/cart/components/line-item-customization-editor"
import {
  ShoppingCart,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Minus,
  Plus,
  Lock,
  Loader2,
} from "lucide-react"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState, useCallback } from "react"
import { createPortal } from "react-dom"

/* ─── Promo Banner Carousel ──────────────────────────────────────────────── */

function PromoBannerCarousel() {
  const [index, setIndex] = useState(0)
  const slides = CART_PROMO_SLIDES

  const prev = () => setIndex((i) => (i === 0 ? slides.length - 1 : i - 1))
  const next = () => setIndex((i) => (i === slides.length - 1 ? 0 : i + 1))

  useEffect(() => {
    const timer = setInterval(next, 4000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const slide = slides[index]

  return (
    <div className="bg-brand-navy text-white flex items-center px-2 py-2 text-xs">
      <button
        onClick={prev}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
        aria-label="Previous promo"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div className="flex-1 text-center leading-tight">
        <span>{slide.text}</span>
        {slide.highlight && (
          <span className="ml-1 inline-block bg-brand-accent text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            {slide.highlight}
          </span>
        )}
        {slide.cta && (
          <span className="ml-1 text-gray-300">{slide.cta}</span>
        )}
      </div>
      <button
        onClick={next}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
        aria-label="Next promo"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  )
}

/* ─── Size Select ────────────────────────────────────────────────────────── */

function SizeSelect({
  productId,
  currentVariantId,
  currentLabel,
  updating,
  onSelectSize,
}: {
  productId: string
  currentVariantId: string
  /** Size label snapshotted on the line item itself (`item.variant_title`) —
   * always available even when the product no longer exists in the native
   * catalog, so the dropdown has something real to show immediately. */
  currentLabel: string
  updating: boolean
  onSelectSize: (variantId: string) => Promise<void>
}) {
  const [variants, setVariants] = useState<HttpTypes.StoreProductVariant[]>([])
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    listProducts({ countryCode: "gb", queryParams: { id: [productId], limit: 1 } })
      .then(({ response }) => setVariants(response.products[0]?.variants ?? []))
  }, [productId])

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  // Only offer switchable alternatives when the native catalog actually has
  // other variants for this product — otherwise there's nothing real to
  // switch to, so the listbox just confirms the current size.
  const hasAlternatives = variants.length > 0
  const matchedVariant = variants.find((v) => v.id === currentVariantId)
  const displayLabel = matchedVariant ? getSizeFromVariant(matchedVariant) : currentLabel

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !updating && setOpen((prev) => !prev)}
        disabled={updating}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change size"
        className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-full pl-3 pr-2.5 py-1.5 text-xs text-gray-800 outline-none focus-visible:ring-1 focus-visible:ring-brand-navy disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="whitespace-nowrap">{displayLabel}</span>
        <ChevronDown
          className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-10 top-full left-0 mt-1 min-w-[120px] max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {hasAlternatives ? (
            variants.map((v) => {
              const size = getSizeFromVariant(v)
              const inStock = isVariantInStock(v)
              return (
                <button
                  key={v.id}
                  type="button"
                  role="option"
                  aria-selected={v.id === currentVariantId}
                  disabled={!inStock}
                  onClick={() => {
                    setOpen(false)
                    onSelectSize(v.id)
                  }}
                  className={`w-full text-left px-3 py-1.5 text-xs whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    v.id === currentVariantId ? "text-brand-navy font-semibold" : "text-gray-700"
                  }`}
                >
                  {size}{!inStock ? " (out of stock)" : ""}
                </button>
              )
            })
          ) : (
            <button
              type="button"
              role="option"
              aria-selected="true"
              onClick={() => setOpen(false)}
              className="w-full text-left px-3 py-1.5 text-xs whitespace-nowrap text-brand-navy font-semibold"
            >
              {displayLabel}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── Cart Item Card ─────────────────────────────────────────────────────── */

function CartItemCard({
  item,
  feesTotal = 0,
  currencyCode,
  onClose,
}: {
  item: HttpTypes.StoreCartLineItem
  /** Personalisation/patch fee total to fold into this item's displayed price (see groupCustomizationFees) */
  feesTotal?: number
  currencyCode: string
  onClose: () => void
}) {
  const [updating, setUpdating] = useState(false)
  const [editingCustomization, setEditingCustomization] = useState(false)

  const changeQuantity = async (quantity: number) => {
    setUpdating(true)
    await updateLineItem({ lineId: item.id, quantity })
    setUpdating(false)
  }

  const unitCurrent = ((item.total ?? 0) + feesTotal) / item.quantity
  const unitOriginal = ((item.original_total ?? 0) + feesTotal) / item.quantity
  const hasDiscount = unitCurrent < unitOriginal
  const discountPct =
    hasDiscount && unitOriginal > 0
      ? Math.round(((unitOriginal - unitCurrent) / unitOriginal) * 100)
      : 0

  const productId = item.product_id

  return (
    <div className="px-4 py-3.5" data-testid="cart-item">
      <div className="flex gap-3">
        {/* Product image */}
        <LocalizedClientLink
          href={getProductUrl(item.product_handle)}
          className="relative w-16 h-20 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden"
          onClick={onClose}
        >
          <Thumbnail
            thumbnail={item.thumbnail}
            images={item.variant?.product?.images}
            size="square"
          />
        </LocalizedClientLink>

        {/* Details */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {/* Title + price */}
          <div className="flex items-start justify-between gap-2">
            <LocalizedClientLink
              href={getProductUrl(item.product_handle)}
              className="text-xs font-semibold text-gray-900 hover:text-brand-navy transition-colors line-clamp-2 leading-snug"
              onClick={onClose}
              data-testid="product-link"
            >
              {item.title}
            </LocalizedClientLink>
            <div className="flex-shrink-0 text-right">
              <span className="text-sm font-bold text-gray-900 block" data-testid="cart-item-price">
                {convertToLocale({ amount: unitCurrent, currency_code: currencyCode })}
              </span>
              {hasDiscount && (
                <span className="text-[10px] text-brand-accent font-medium">
                  -{discountPct}%
                </span>
              )}
            </div>
          </div>

          {/* Edit customisation — expands inline, no page navigation */}
          <button
            type="button"
            onClick={() => setEditingCustomization((prev) => !prev)}
            className="inline-block self-start px-3 py-1 text-[10px] font-semibold text-white bg-brand-primary rounded-full hover:bg-brand-navy transition-colors"
          >
            Edit customisation
          </button>

          {editingCustomization && (
            <LineItemCustomizationEditor
              item={item}
              currencyCode={currencyCode}
              onClose={() => setEditingCustomization(false)}
              compact
            />
          )}

          {/* Qty + Size */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Quantity stepper */}
            <div className="flex items-center border border-gray-200 rounded-full">
              <button
                className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-40 rounded-full"
                onClick={() => changeQuantity(item.quantity - 1)}
                disabled={item.quantity <= 1 || updating}
                aria-label="Decrease quantity"
              >
                <Minus className="w-2.5 h-2.5" />
              </button>
              <span className="w-5 text-center text-xs font-medium" data-testid="cart-item-quantity">
                {updating
                  ? <Loader2 className="w-3 h-3 animate-spin mx-auto" />
                  : item.quantity
                }
              </span>
              <button
                className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-40 rounded-full"
                onClick={() => changeQuantity(item.quantity + 1)}
                disabled={item.quantity >= 10 || updating}
                aria-label="Increase quantity"
              >
                <Plus className="w-2.5 h-2.5" />
              </button>
            </div>

            {/* Size select */}
            {productId && item.variant_id && (
              <SizeSelect
                productId={productId}
                currentVariantId={item.variant_id}
                currentLabel={item.variant_title ?? item.title}
                updating={updating}
                onSelectSize={async (newVariantId) => {
                  if (newVariantId === item.variant_id) return
                  setUpdating(true)
                  const { changeSizeAction } = await import("@modules/cart/actions")
                  await changeSizeAction({
                    lineId: item.id,
                    originalVariantId: item.variant_id!,
                    newVariantId,
                    quantity: item.quantity,
                    metadata: item.metadata ?? null,
                  })
                  setUpdating(false)
                }}
              />
            )}

            {/* Remove */}
            <DeleteButton
              id={item.id}
              className="ml-auto w-auto text-gray-400 hover:text-brand-accent transition-colors"
              data-testid="cart-item-remove-button"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Empty Cart State ───────────────────────────────────────────────────── */

function EmptyCartState({
  onClose,
}: {
  onClose: () => void
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center">
      <ShoppingCart className="w-12 h-12 text-gray-200 mb-4" strokeWidth={1} />
      <h3 className="text-base font-bold text-gray-900 mb-1">Your cart is empty</h3>
      <p className="text-sm text-gray-500 mb-6">Add items to get started</p>
      <LocalizedClientLink
        href="/store"
        onClick={onClose}
        className="px-6 py-2.5 bg-brand-navy text-white text-sm font-semibold rounded-lg hover:bg-brand-primary-dark transition-colors"
      >
        Browse Products
      </LocalizedClientLink>
    </div>
  )
}

/* ─── Main CartDropdown ──────────────────────────────────────────────────── */

const CartDropdown = ({
  cart: cartState,
  shippingOptions = [],
}: {
  cart?: HttpTypes.StoreCart | null
  recommendedProducts?: HttpTypes.StoreProduct[]
  shippingOptions?: HttpTypes.StoreCartShippingOption[]
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [visible, setVisible] = useState(false)

  // Personalisation/patch fees are their own cart line items — group them
  // back into their parent product so the drawer shows one card per product
  // (not a separate card per fee) and don't count them toward the item badge.
  const groupedItems = cartState?.items
    ? groupCustomizationFees(cartState.items)
    : []

  const totalItems = groupedItems.reduce((acc, { item }) => acc + item.quantity, 0)

  const itemRef = useRef<number>(totalItems || 0)
  const pathname = usePathname()

  const open = useCallback(() => {
    setIsOpen(true)
    document.body.style.overflow = "hidden"
    requestAnimationFrame(() => setVisible(true))
  }, [])

  const close = useCallback(() => {
    setVisible(false)
    setTimeout(() => {
      setIsOpen(false)
      document.body.style.overflow = ""
    }, 300)
  }, [])

  // Auto-open when items are added (never auto-close)
  useEffect(() => {
    const prevItems = itemRef.current
    itemRef.current = totalItems
    if (totalItems > prevItems && !pathname.includes("/cart")) {
      open()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItems])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [isOpen, close])

  // This component (trigger + portaled drawer) is mounted once, in the
  // desktop header, and stays mounted even when that header is hidden via
  // CSS on mobile viewports. The mobile header's own cart icon can't safely
  // mount a second CartDropdown instance (its portal would escape the
  // display:none wrapper and duplicate the drawer), so it dispatches this
  // event instead and lets the one existing instance open.
  useEffect(() => {
    const handleOpenRequest = () => open()
    window.addEventListener(CART_DRAWER_OPEN_EVENT, handleOpenRequest)
    return () => window.removeEventListener(CART_DRAWER_OPEN_EVENT, handleOpenRequest)
  }, [open])

  // Cleanup overflow on unmount
  useEffect(() => {
    return () => { document.body.style.overflow = "" }
  }, [])

  const hasItems = groupedItems.length > 0

  const subtotal = cartState?.subtotal ?? 0
  const currencyCode = cartState?.currency_code ?? "gbp"

  const {
    label: shippingLabel,
    amount: shippingTotal,
    isAttached: hasShippingMethod,
  } = getShippingEstimate(cartState, shippingOptions)

  const total = hasShippingMethod ? (cartState?.total ?? subtotal) : subtotal + shippingTotal

  const checkoutHref = cartState
    ? `/checkout?step=${getCheckoutStep(cartState)}`
    : "/checkout"

  return (
    <>
      {/* Cart Icon Button */}
      <button
        onClick={open}
        className="flex items-center gap-1.5 text-gray-900 hover:opacity-80 transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 rounded px-2 py-1.5"
        aria-label={`Shopping cart with ${totalItems} items`}
        data-testid="nav-cart-link"
      >
        <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-sm">Cart ({totalItems})</span>
      </button>

      {/* Slide-in Drawer — portaled to body to escape nav stacking context */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-drawer">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${
              visible ? "opacity-40" : "opacity-0"
            }`}
            onClick={close}
          />

          {/* Panel */}
          <div
            className={`absolute top-0 right-0 h-full w-full max-w-[480px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
              visible ? "translate-x-0" : "translate-x-full"
            }`}
            data-testid="nav-cart-dropdown"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                  Cart
                </h2>
                {totalItems > 0 && (
                  <span className="bg-brand-accent text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                    {totalItems}
                  </span>
                )}
              </div>
              <button
                onClick={close}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close cart"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            {hasItems ? (
              <>
                {/* Promo Banner */}
                <PromoBannerCarousel />

                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                  {[...groupedItems]
                    .sort((a, b) =>
                      (a.item.created_at ?? "") > (b.item.created_at ?? "") ? -1 : 1
                    )
                    .map(({ item, feesTotal }) => (
                      <CartItemCard
                        key={item.id}
                        item={item}
                        feesTotal={feesTotal}
                        currencyCode={currencyCode}
                        onClose={close}
                      />
                    ))}
                  <div className="px-4 py-3.5">
                    <MysteryBox />
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 px-4 py-4 bg-white space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="font-medium text-gray-900" data-testid="cart-subtotal">
                      {convertToLocale({ amount: subtotal, currency_code: currencyCode })}
                    </span>
                  </div>
                  {shippingLabel && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Shipping ({shippingLabel})</span>
                      <span className="font-medium text-gray-900">
                        {convertToLocale({ amount: shippingTotal, currency_code: currencyCode })}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-bold pt-1 border-t border-gray-100">
                    <span>Total</span>
                    <span>{convertToLocale({ amount: total, currency_code: currencyCode })}</span>
                  </div>

                  <LocalizedClientLink
                    href={checkoutHref}
                    onClick={close}
                    className="mt-1 flex items-center justify-center gap-2 w-full py-3 bg-brand-navy text-white text-sm font-bold rounded-lg hover:bg-brand-primary-dark transition-colors"
                    data-testid="go-to-cart-button"
                  >
                    <Lock className="w-4 h-4" />
                    Checkout
                  </LocalizedClientLink>
                </div>
              </>
            ) : (
              <EmptyCartState onClose={close} />
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default CartDropdown
